<template>
    <InsightCardLayout hide-bar :data-insight-id="insightId">
        <!-- Category badge -->
        <div class="flex items-center gap-2 mb-4">
            <BaseChip v-if="exampleLabel" size="xs" class="self-start text-ink-muted text-pretty">{{ t("components.insightCard.example", { label: exampleLabel }) }}</BaseChip>
            <InsightCategoryLabel v-else-if="experimentTitle" :label="experimentTitle" :experiment-slug="experimentSlug" variant="chip" />
            <BaseButton
                v-if="showClose"
                type="button"
                theme="tertiary"
                size="sm"
                icon-only
                class="ml-auto shrink-0"
                :aria-label="t('components.insightCard.dismiss')"
                @click="emit('close')">
                <i class="bi bi-x-lg text-[13px]" aria-hidden="true"></i>
            </BaseButton>
        </div>

        <!-- Title -->
        <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
        <h3 tabindex="-1" data-insight-heading class="font-serif font-medium text-[20px] leading-[1.16] text-ink text-pretty mb-4">
            <MarkdownText :text="title" inline />
        </h3>

        <!-- Divider -->
        <div class="border-t border-border mb-4" role="separator"></div>

        <!-- Summary with inline "See more" -->
        <div
            class="summary-with-toggle body-small prose prose-sm max-w-none text-ink text-pretty leading-relaxed prose-headings:my-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-5">
            <MarkdownText :text="summary" />
            <BaseButton
                v-if="hasExpandableContent && showSeeMore"
                type="button"
                theme="tertiary"
                size="sm"
                :aria-expanded="isExpanded"
                :aria-controls="`insight-detail-${insightId}`"
                @click="isExpanded = !isExpanded">
                {{ isExpanded ? t("components.insightCard.seeLess") : t("components.insightCard.seeMore") }}
            </BaseButton>
        </div>

        <!-- Expandable detail box -->
        <div v-if="isExpanded" :id="`insight-detail-${insightId}`" class="rounded-2xl border border-border bg-surface-sunken px-4 py-2.5 my-2.5 flex flex-col gap-3">
            <div v-if="content" class="detail-with-label prose prose-sm max-w-none text-ink text-pretty leading-relaxed prose-p:my-1 prose-ul:my-1 prose-ol:my-1">
                <span class="detail-label font-bold">{{ t("components.insightCard.whyFlagging") + " " }}</span>
                <MarkdownText :text="content" />
            </div>
        </div>

        <!-- Unknowns -->
        <div v-if="unknowns.length" class="flex flex-col gap-2 mt-4">
            <span class="body-small text-ink text-pretty">{{ t("components.insightCard.wantToUnderstand") }}</span>
            <ul class="list-disc pl-5">
                <li v-for="unknown in unknowns" :key="unknown" class="body-small text-ink text-pretty leading-relaxed">
                    {{ unknown }}
                </li>
            </ul>
        </div>

        <!-- Footer: Based on links -->
        <div class="mt-3 flex items-center justify-between gap-4">
            <InsightEvidenceChips :evidence-groups="evidenceGroups" :analytics-context="{ insightId, experimentSlug, location }" appearance="links" />
        </div>

        <slot name="feedback" />
    </InsightCardLayout>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import type { LabInsightLocation } from "~vue/composables/useLabInsightActions";
import type { InsightEvidenceGroup } from "~vue/Labs/evidence";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    experimentTitle: string;
    experimentSlug: string;
    title: string;
    summary: string;
    content?: string;
    insightId: string;
    unknowns?: string[];
    showSeeMore?: boolean;
    evidenceGroups?: InsightEvidenceGroup[];
    /** Where the card is rendered, for evidence-chip analytics context. */
    location?: LabInsightLocation;
    /** Offers a dismiss (X) button in the badge row. Homepage-only; My Work page omits it. */
    showClose?: boolean;
    /** Renders a neutral "Example: {label}" badge instead of the tier-colored category chip, for
     * illustrative example insights that aren't a real category. */
    exampleLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
    content: "",
    unknowns: () => [],
    showSeeMore: false,
    location: "mywork",
    showClose: false,
});

const emit = defineEmits<{ close: [] }>();

const isExpanded = ref(false);
const hasExpandableContent = computed(() => Boolean(props.content));
</script>

<style scoped>
/* Make the last paragraph from MarkdownText render inline so the
   "See more" button flows on the same line as the summary text.
   Chain: wrapper → MarkdownText root (inline) → [data-marked] div (inline) → last <p> (inline) → button (inline) */
.summary-with-toggle :deep([data-marked]) {
    display: inline;
}

.summary-with-toggle :deep([data-marked] > p:last-of-type) {
    display: inline;
}

/* Make the bold label flow inline with the first paragraph of the detail content. */
.detail-with-label .detail-label {
    display: inline;
}

.detail-with-label :deep([data-marked]) {
    display: inline;
}

.detail-with-label :deep([data-marked] > p:first-of-type) {
    display: inline;
}
</style>
